import React, { Fragment } from 'react'

import { CompositionBar } from '@contrast-security-inc/design-system-data-bars'
import { IconConfirmation } from '@contrast-security-inc/design-system-foundations'
import { css } from '@emotion/css'
import { Vulnerability } from '../../../../components/shared'
import { CvssScoringType } from '../../helpers/ui-interfaces'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { useIntl } from 'react-intl'

const cellCss = css`
  display: flex;
  flex-grow: 1;
  align-items: center;
  padding: 0 2px;
`

const thermometerContainerCss = css`
  flex-basis: 100%;
  margin-right: 0.5rem;
  margin-top: 23px;
  @media (max-width: 768px) {
    display: none;
  }
  cursor: pointer;
`

const vulnTotalCountContainerCss = css`
  flex: none;
  vertical-align: text-bottom;
  text-align: center;
  display: inline-block;
  margin-left: 0.5rem;
`
const vulnCountContainerCss = css`
  flex: none;
  min-width: 54px;
  margin-left: 0.16px;
  text-align: left;
  @media (max-width: 768px) {
    text-align: center;
  }
`

const countCss = css`
  line-height: 1em;
`

export const getSeverityData = (
  vulnerabilities,
  cvssScoringType: CvssScoringType = CvssScoringType.CVSS2
) => {
  const severityData = {
    critical: { total: 0, open: 0, vulnerabilities: [] },
    high: { total: 0, open: 0, vulnerabilities: [] },
    medium: { total: 0, open: 0, vulnerabilities: [] },
    low: { total: 0, open: 0, vulnerabilities: [] },
    note: { total: 0, open: 0, vulnerabilities: [] },
    open: 0,
    total: 0
  }

  if (vulnerabilities) {
    vulnerabilities.forEach((vulnerability) => {
      const severityCode =
        cvssScoringType === CvssScoringType.CVSS3_1 &&
        vulnerability.cvss3SeverityCode &&
        vulnerability.hasCvss3
          ? vulnerability.cvss3SeverityCode
          : vulnerability.severityCode

      if (severityCode) {
        severityData[severityCode.toLowerCase()].total++
        severityData[severityCode.toLowerCase()].vulnerabilities.push(
          vulnerability
        )
        severityData.total++
      }
    })
  }

  return severityData
}

export type VulnerabilitiesCellVulnerability = Omit<
  Vulnerability,
  | 'accessVector'
  | 'accessComplexity'
  | 'availabilityImpact'
  | 'confidentialityImpact'
  | 'integrityImpact'
  | 'authentication'
>

type Props = {
  rowData: {
    vulnerabilities: VulnerabilitiesCellVulnerability[]
    name
  }
  onClick?: (props?: any) => void
  cvssScoringType?: CvssScoringType
  isPrivate?: boolean
}

export function VulnerabilitiesCell({
  rowData: { vulnerabilities, name },
  cvssScoringType,
  onClick,
  isPrivate = false
}: Props) {
  const { formatMessage } = useIntl()
  const severityData = getSeverityData(vulnerabilities, cvssScoringType)

  return (
    <Fragment>
      <div className={cellCss}>
        {severityData.total > 0 && (
          <div
            data-testid={`grid-library-vulnerabilities-${name}`}
            className={thermometerContainerCss}
            onClick={() => {
              if (onClick) {
                onClick()
              }
            }}
          >
            <CompositionBar
              segments={[
                {
                  TooltipProps: {
                    body: formatMessage({
                      id: 'title_vulnSeverityTypeCritical'
                    })
                  },
                  color: 'red50',
                  value: severityData.critical.total
                },
                {
                  TooltipProps: {
                    body: formatMessage({
                      id: 'title_vulnSeverityTypeHigh'
                    })
                  },
                  color: 'orange50',
                  value: severityData.high.total
                },
                {
                  TooltipProps: {
                    body: formatMessage({
                      id: 'title_vulnSeverityTypeMedium'
                    })
                  },
                  color: 'yellow50',
                  value: severityData.medium.total
                },
                {
                  TooltipProps: {
                    body: formatMessage({
                      id: 'title_vulnSeverityTypeLow'
                    })
                  },
                  color: 'neutral40',
                  value: severityData.low.total
                },
                {
                  TooltipProps: {
                    body: formatMessage({
                      id: 'title_vulnSeverityTypeNote'
                    })
                  },
                  color: 'neutral30',
                  value: severityData.note.total
                }
              ]}
              size="sm"
            />
          </div>
        )}
        {!isPrivate ? (
          <div className={vulnCountContainerCss}>
            <div className={vulnTotalCountContainerCss}>
              <span className={countCss}>
                {severityData.total > 0 ? (
                  `(${severityData.total})`
                ) : (
                  <Stack alignItems="center">
                    <IconConfirmation color="green70" />
                    {formatMessage({ id: 'detail_libraries_noneIdentified' })}
                  </Stack>
                )}
              </span>
            </div>
          </div>
        ) : (
          <div
            className={css`
              padding: 0 2px 0 8px;
            `}
          >
            {formatMessage({ id: 'title_unknown' })}
          </div>
        )}
      </div>
    </Fragment>
  )
}
